import axios from 'axios';
import { BASE_URI_DEVELOPMENT_CENTER } from '@/api/CONST.js';

const PREFIX = BASE_URI_DEVELOPMENT_CENTER;

/**
 * @param {object} data
 * @param {string|number} data.courseId
 * @param {object} data.params
 * @returns Promise
 */
export const getCourseLayersList = ({ courseId, params }) => axios.get(`${PREFIX}/courses/${courseId}/layers`, { params });

/**
 * @param {object} data
 * @param {string|number} data.courseId
 * @param {string|number} data.layerId
 * @returns Promise
 */
export const getCourseLayer = ({ courseId, layerId }) => axios.get(`${PREFIX}/courses/${courseId}/layers/${layerId}`);

/**
 * @param {object} data
 * @param {string|number} data.courseId
 * @param {object} data.layer
 * @returns Promise
 */
export const updateCourseLayer = ({ courseId, layerId, layer }) => axios.put(`${PREFIX}/courses/${courseId}/layers/${layerId}`, layer);

/**
 * @param {object} data
 * @param {string|number} data.courseId
 * @param {object} data.layerId
 * @returns Promise
 */
export const removeCourseLayer = ({ courseId, layerId }) => axios.delete(`${PREFIX}/courses/${courseId}/layers/${layerId}`);

/**
 * @param {object} data
 * @param {string|number} data.courseId
 * @param {object} data.layerId
 * @returns Promise
 */
export const restoreCourseLayer = ({ courseId, layerId }) => axios.post(`${PREFIX}/courses/${courseId}/layers/${layerId}/restore`);


/**
 * @param {object} data
 * @param {string|number} data.courseId
 * @returns Promise
 */
export const addCourseLayer = ({ courseId, groupsCount }) => axios.post(`${PREFIX}/courses/${courseId}/layers`, { groups_count: groupsCount });

/**
 * @param {object} data
 * @param {string|number} data.courseId
 * @param {File} data.file
 * @returns Promise
 */
export const uploadCourseLayersTemplate = ({ courseId, file }) => {
    const fileData = new FormData();

    fileData.append('file', file);

    return axios.post(`${PREFIX}/courses/${courseId}/layers/import`, fileData);
};

/**
 * @returns Promise
 */
export const downloadCourseLayersTemplate = () => axios.get(`${PREFIX}/courses/layers/import/template`, { responseType: 'blob' });

/**
 * @param {object} data
 * @param {string|number} data.courseId
 * @param {string|number} data.layerId
 * @param {Array<string|number>} data.groups
 * @returns {Promise}
 */
export const deleteLayerGroups = ({
    courseId,
    layerId,
    groups,
}) => axios.delete(
    `${PREFIX}/courses/${courseId}/layers/${layerId}/groups`,
    { data: { groups } },
);

/**
 * @param {object} data
 * @param {string|number} data.courseId
 * @param {string|number} data.layerId
 * @returns {Promise}
 */
export const createLayerGroup = ({ courseId, layerId }) => axios.post(`${PREFIX}/courses/${courseId}/layers/${layerId}/groups`);

/**
 * @param {object} data
 * @param {string|number} data.courseId
 * @param {string|number} data.layerId
 * @param {string} data.query
 * @param {string|number} data.page
 * @returns {Promise}
 */
export const getLayerGroups = ({ courseId, layerId, query, page }) => axios.get(`${PREFIX}/courses/${courseId}/layers/${layerId}/groups`, {
    params: {
        query,
        page,
    },
});

/**
 * @param {object} data
 * @param {string|number} data.courseId
 * @param {string|number} data.layerId
 * @param {Array<string|number>} data.groups
 * @returns {Promise}
 */
export const restoreLayerGroups = ({ courseId, layerId, groups }) => axios.post(
    `${PREFIX}/courses/${courseId}/layers/${layerId}/groups/restore`,
    { groups },
);

/**
 * @param {object} data
 * @param {string|number} data.courseId
 * @param {string|number} data.layerId
 * @param {string|number} data.groupId
 * @param {string} data.name
 * @returns {Promise}
 */
export const updateLayerGroup = ({ courseId, layerId, groupId, name }) => axios.put(
    `${PREFIX}/courses/${courseId}/layers/${layerId}/groups/${groupId}`,
    { name },
);

/**
 * @param {object} data
 * @param {string|number} data.courseId
 * @param {string|number} data.layerId
 * @param {string|number} data.groupId
 * @param {Array<string|number>} data.students
 * @returns {Promise}
 */
export const deleteLayerGroupStudents = ({ courseId, layerId, groupId, students }) => axios.delete(
    `${PREFIX}/courses/${courseId}/layers/${layerId}/groups/${groupId}/students`,
    { data: { students } },
);


/**
 * @param {object} data
 * @param {string|number} data.courseId
 * @param {string|number} data.layerId
 * @param {string|number} data.groupId
 * @param {Array<string|number>} data.students
 * @returns {Promise}
 */
export const addLayerGroupStudents = ({ courseId, groupId, layerId, students }) => axios.put(
    `${PREFIX}/courses/${courseId}/layers/${layerId}/groups/${groupId}/students`,
    { students },
);

/**
 * @param {object} data
 * @param {string|number} data.courseId
 * @param {string|number} data.layerId
 * @param {string|number} data.groupId
 * @param {string|number} data.page
 * @returns {Promise}
 */
export const getLayerGroupStudents = ({ courseId, layerId, groupId, page }) => axios.get(`${PREFIX}/courses/${courseId}/layers/${layerId}/groups/${groupId}/students`, { params: { page } });

/**
 * @param {number|string} courseId
 * @param {number|string} layerId
 * @param {Object} params
 * @param {string} params.search
 * @param {number} params.page
 * @param {Object} config
 * @returns {Promise<AxiosResponse<any>>}
 */
export const getLayerNotDistibutedStudents = ({
    courseId,
    layerId,
    params,
    config,
}) => axios.get(
    `${BASE_URI_DEVELOPMENT_CENTER}/courses/${courseId}/layers/${layerId}/students/not-distributed`,
    {
        params,
        ...config,
    },
);

/**
 * @param {number|string} courseId
 * @param {number|string} layerId
 * @param {Object} params
 * @param {string} params.search
 * @param {number} params.page
 * @param {Object} config
 * @returns {Promise<AxiosResponse<any>>}
 */
export const getLayerDistibutedStudents = ({
    courseId,
    layerId,
    params,
    config,
}) => axios.get(
    `${BASE_URI_DEVELOPMENT_CENTER}/courses/${courseId}/layers/${layerId}/students/distributed`,
    {
        params,
        ...config,
    },
);

/**
 * @param {number|string} courseId
 * @param {number|string} layerId
 * @param {number|string} groupId
 * @param {boolean} isAttachAllStudents
 * @param {Array} except
 * @param {Array} students
 * @returns {Promise<AxiosResponse<any>>}
 */
export const addStudentsToLayerGroup = ({ courseId, layerId, groupId, isAttachAllStudents, students, except }) => axios.put(`${BASE_URI_DEVELOPMENT_CENTER}/courses/${courseId}/layers/${layerId}/groups/${groupId}/students`, {
    attach_all_students: isAttachAllStudents,
    students,
    except,
});

/**
 * @param {number|string} courseId
 * @param {number|string} layerId
 * @param {Array[]} groups
 * @returns {Promise<AxiosResponse<any>>}
 */
export const deleteManyLayerGroups = ({ courseId, layerId, groups }) => axios.delete(
    `${PREFIX}/courses/${courseId}/layers/${layerId}/destroy-many`,
    { data: { groups } },
);

/**
 * @param {number|string} courseId
 * @param {number|string} layerId
 * @param {string} restore_token
 * @returns {Promise<AxiosResponse<any>>}
 */
export const restoreManyLayerGroups = ({ courseId, layerId, restore_token }) => axios.post(
    `${PREFIX}/courses/${courseId}/layers/${layerId}/restore-many`,
    { restore_token },
);

/**
 * @param {object} data
 * @param {string|number} data.course
 * @returns Promise
 */
export const createLayersReport = ({ course }) => axios.get(`${PREFIX}/courses/${course}/layers/report`);
